import {
  BoxProps,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  usePrevious,
} from "@chakra-ui/react";
import { RefObject, useEffect, useRef } from "react";
import { Phrase, Student } from "../db";
import StudentHeader from "./StudentHeader";
import WaveformImage from "./WaveformImage";
import PhraseHeader from "./PhraseHeader";
import { LayoutOption, RecordingOverrideControls } from "../Workbench";

interface DataTableProps extends BoxProps {
  students: Student[];
  phrases: Phrase[];
  activeStudentId?: number;
  activePhraseId?: number;
  onChangeActiveRecording: (student: Student, phrase: Phrase) => void;
  updateStudents: () => void;
  updatePhrases: () => void;
  layout: LayoutOption;
  recordingOverrideCallbackRef: RefObject<RecordingOverrideControls | null>;
}

const DataTable = ({
  students,
  phrases,
  activeStudentId,
  activePhraseId,
  onChangeActiveRecording,
  updateStudents,
  updatePhrases,
  layout,
  recordingOverrideCallbackRef,
  ...props
}: DataTableProps) => {
  const ref = useRef<HTMLTableElement>(null);
  const activeCellRef = useRef<HTMLTableCellElement>(null);
  const previousNumStudents = usePrevious<number>(students.length);
  const previousNumPhrases = usePrevious<number>(phrases.length);

  useEffect(() => {
    if (previousNumStudents && students.length > previousNumStudents) {
      ref.current?.scrollTo(ref.current.scrollWidth, 0);
    }
    if (previousNumPhrases && phrases.length > previousNumPhrases) {
      ref.current?.scrollTo(ref.current.scrollLeft, ref.current.scrollHeight);
    }
  }, [
    onChangeActiveRecording,
    previousNumStudents,
    previousNumPhrases,
    phrases,
    students,
  ]);

  // Scroll active cell into view.
  useEffect(() => {
    if (ref.current && activeCellRef.current) {
      if (layout === "horizontal") {
        activeCellRef.current.scrollIntoView({
          block: "end",
          inline: "end",
          behavior: "smooth",
        });
      } else {
        const tableRect = ref.current.getBoundingClientRect();
        const activeCellRect = activeCellRef.current.getBoundingClientRect();
        let x = ref.current.scrollLeft;
        let y = ref.current.scrollTop;
        if (
          activeCellRect.right > tableRect.right ||
          activeCellRect.left < tableRect.left
        ) {
          x = ref.current.scrollLeft + (activeCellRect.right - tableRect.right);
        }
        ref.current?.scrollTo({ left: x, top: y, behavior: "smooth" });
      }
    }
  }, [activeStudentId, activePhraseId, layout]);

  const borderWidth = "1px";
  const borderColor = "grey.500";
  return (
    <Table ref={ref} variant="unstyled" {...props}>
      <Thead>
        <Tr>
          <Th />
          {students.map((student) => (
            <StudentHeader
              key={student.id}
              student={student}
              updateStudents={updateStudents}
              borderWidth={borderWidth}
              borderColor={borderColor}
            />
          ))}
        </Tr>
      </Thead>
      <Tbody>
        {phrases.map((phrase) => {
          const phraseHeading = (
            <PhraseHeader
              key={phrase.id}
              phrase={phrase}
              updatePhrases={updatePhrases}
              borderWidth={borderWidth}
              borderColor={borderColor}
            />
          );
          const studentCells = students.map((student) => {
            const active =
              student.id === activeStudentId && phrase.id === activePhraseId;
            return (
              <Td
                ref={active ? activeCellRef : undefined}
                key={`${student.id}-${phrase}`}
                borderWidth={borderWidth}
                borderColor={borderColor}
                outline={active ? "2px solid" : "0px"}
                outlineOffset="-2px"
                outlineColor="blue.500"
                cursor="pointer"
                onClick={() => {
                  recordingOverrideCallbackRef.current?.forceStop();
                  onChangeActiveRecording(student, phrase);
                }}
                textAlign="center"
                position="relative"
                zIndex={active ? 10 : 0}
                py={1}
                px={1}
              >
                {student.phrases.includes(phrase.text) ? (
                  <WaveformImage studentId={student.id!} phrase={phrase.text} />
                ) : (
                  <Text fontSize="xs" color="gray.500">
                    No data
                  </Text>
                )}
              </Td>
            );
          });
          return (
            <Tr key={phrase.id} border="none">
              {[phraseHeading, ...studentCells]}
            </Tr>
          );
        })}
      </Tbody>
    </Table>
  );
};

export default DataTable;
